import React, { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import api from "../lib/api";
import { Sprout, MapPin, PlusCircle, ShieldCheck, LogOut, User, Menu, X, BookOpen, ChevronDown } from "lucide-react";
import { Button } from "./ui/button";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem
} from "./ui/dropdown-menu";

const navLinkClass = ({ isActive }) =>
  `text-sm font-bold transition-colors ${
    isActive ? "text-brand-primary" : "text-brand-text hover:text-brand-primary"
  }`;

export default function Header() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [petition, setPetition] = useState(null);
  const [topics, setTopics] = useState([]);

  useEffect(() => {
    api.get("/banners").then((r) => {
      const p = (r.data || []).find((b) => b.banner_id === "petition");
      setPetition(p || null);
    }).catch(() => {});
    api.get("/topics").then((r) => setTopics(r.data || [])).catch(() => {});
  }, []);

  const isMod = user && (user.role === "moderator" || user.role === "admin");
  const showPetition = petition && petition.enabled !== false && petition.button_label && petition.button_link;

  return (
    <header
      data-testid="site-header"
      className="backdrop-blur-xl bg-[#FDFBF7]/90 sticky top-0 z-50 border-b border-[#E5E0D8]/60"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between gap-4">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-2">
          <span className="w-10 h-10 rounded-2xl bg-brand-primary text-white flex items-center justify-center shadow-sm">
            <Sprout className="w-5 h-5" />
          </span>
          <div className="leading-tight">
            <div className="font-heading font-black text-lg text-brand-text">Cela Seguro</div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-brand-textMuted">Brasil</div>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          <NavLink to="/explorar" data-testid="nav-explore" className={navLinkClass}>
            <span className="inline-flex items-center gap-1.5"><MapPin className="w-4 h-4" /> Explorar</span>
          </NavLink>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="nav-topics" className="text-sm font-bold text-brand-text hover:text-brand-primary inline-flex items-center gap-1.5">
                <BookOpen className="w-4 h-4" /> Guias <ChevronDown className="w-3.5 h-3.5" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="bg-white border border-brand-border rounded-2xl p-2 shadow-lg">
              {topics.length === 0 ? (
                <>
                  <DropdownMenuItem asChild><Link to="/topico/celiaco" data-testid="nav-topic-celiaco" className="cursor-pointer rounded-xl px-3 py-2 font-bold text-brand-text">🌾 Celíaco</Link></DropdownMenuItem>
                  <DropdownMenuItem asChild><Link to="/topico/sem_lactose" data-testid="nav-topic-sem-lactose" className="cursor-pointer rounded-xl px-3 py-2 font-bold text-brand-text">🥛 Sem Lactose</Link></DropdownMenuItem>
                  <DropdownMenuItem asChild><Link to="/topico/vegano" data-testid="nav-topic-vegano" className="cursor-pointer rounded-xl px-3 py-2 font-bold text-brand-text">🌱 Vegano</Link></DropdownMenuItem>
                </>
              ) : (
                topics.map((t) => (
                  <DropdownMenuItem key={t.topic_id} asChild>
                    <Link to={`/topico/${t.topic_id}`} data-testid={`nav-topic-${t.topic_id}`} className="cursor-pointer rounded-xl px-3 py-2 font-bold text-brand-text">
                      {t.topic_id === "celiaco" ? "🌾 " : t.topic_id === "sem_lactose" ? "🥛 " : t.topic_id === "vegano" ? "🌱 " : "📖 "}{t.title}
                    </Link>
                  </DropdownMenuItem>
                ))
              )}
            </DropdownMenuContent>
          </DropdownMenu>
          <NavLink to="/cadastrar-lugar" data-testid="nav-submit" className={navLinkClass}>
            <span className="inline-flex items-center gap-1.5"><PlusCircle className="w-4 h-4" /> Cadastrar Lugar</span>
          </NavLink>
          {isMod && (
            <NavLink to="/moderacao" data-testid="nav-moderation" className={navLinkClass}>
              <span className="inline-flex items-center gap-1.5"><ShieldCheck className="w-4 h-4" /> Moderação</span>
            </NavLink>
          )}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          {showPetition && (
            <a
              href={petition.button_link}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="header-petition-btn"
              className="inline-flex items-center gap-1.5 rounded-full font-bold px-4 py-2 text-sm shadow-sm transition-transform hover:scale-[1.03]"
              style={{ background: petition.button_bg || "#F4B942", color: petition.button_text || "#1C2321" }}
            >
              ✍️ {petition.button_label}
            </a>
          )}
          {user ? (
            <>
              <Link to="/perfil" data-testid="profile-link" className="flex items-center gap-2 text-sm font-bold text-brand-text hover:text-brand-primary">
                {user.picture ? (
                  <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full object-cover" />
                ) : (
                  <span className="w-8 h-8 rounded-full bg-brand-primary/10 text-brand-primary flex items-center justify-center">
                    <User className="w-4 h-4" />
                  </span>
                )}
                <span className="max-w-[120px] truncate">{user.name || user.email}</span>
              </Link>
              <Button
                onClick={async () => { await logout(); navigate("/"); }}
                data-testid="logout-btn"
                variant="outline"
                className="rounded-full border-brand-border text-brand-text hover:bg-brand-primary hover:text-white hover:border-brand-primary"
              >
                <LogOut className="w-4 h-4 mr-1" /> Sair
              </Button>
            </>
          ) : (
            <>
              <Link to="/login" data-testid="login-link" className="text-sm font-bold text-brand-text hover:text-brand-primary">Entrar</Link>
              <Link to="/registrar" data-testid="register-link">
                <Button className="rounded-full bg-brand-primary hover:bg-brand-primaryHover text-white font-bold px-5">
                  Criar conta
                </Button>
              </Link>
            </>
          )}
        </div>

        <button
          data-testid="mobile-menu-toggle"
          className="md:hidden w-10 h-10 flex items-center justify-center rounded-full border border-brand-border bg-white"
          onClick={() => setOpen((o) => !o)}
          aria-label="Menu"
        >
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-brand-border bg-white px-4 py-4 flex flex-col gap-3" data-testid="mobile-menu">
          <Link to="/explorar" onClick={() => setOpen(false)} className="font-bold text-brand-text">Explorar</Link>
          <Link to="/topico/celiaco" onClick={() => setOpen(false)} className="font-bold text-brand-text">🌾 Celíaco</Link>
          <Link to="/topico/sem_lactose" onClick={() => setOpen(false)} className="font-bold text-brand-text">🥛 Sem Lactose</Link>
          <Link to="/topico/vegano" onClick={() => setOpen(false)} className="font-bold text-brand-text">🌱 Vegano</Link>
          <Link to="/cadastrar-lugar" onClick={() => setOpen(false)} className="font-bold text-brand-text">Cadastrar Lugar</Link>
          {showPetition && (
            <a href={petition.button_link} target="_blank" rel="noopener noreferrer" onClick={() => setOpen(false)} className="font-bold text-brand-primary">
              ✍️ {petition.button_label}
            </a>
          )}
          {isMod && <Link to="/moderacao" onClick={() => setOpen(false)} className="font-bold text-brand-text">Moderação</Link>}
          {user ? (
            <>
              <Link to="/perfil" onClick={() => setOpen(false)} className="font-bold text-brand-text">Meu Perfil</Link>
              <button onClick={async () => { await logout(); setOpen(false); navigate("/"); }} className="font-bold text-left text-brand-text">Sair</button>
            </>
          ) : (
            <>
              <Link to="/login" onClick={() => setOpen(false)} className="font-bold text-brand-text">Entrar</Link>
              <Link to="/registrar" onClick={() => setOpen(false)} className="font-bold text-brand-primary">Criar conta</Link>
            </>
          )}
        </div>
      )}
    </header>
  );
}
